import React, { FC, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Children, Container, ContentsCard } from './DomainTabs.styles';
import { TabButton, TabsCont } from '../../components/TabGroup/TabGroup.styles';
import { CreateSurveyCardsNames, CreateSurveyTypeNames } from '../../constans/state';

interface IDomainTabs {
  surveyOverviewType: string;
  defaultTab: string;
  defaultDomain: string;
  applyFilters: (filters: Record<string, string | null | undefined>) => void;
  children: ReactElement;
}

export const clearedFilters = {
  pageNo: null,
  sortOrder: null,
  perPage: null,
  addTeam: null,
  compare: null,
  compareSort: null,
  activeSection: null,
  surveyReport: null,
  prm: null,
  riskAnalysis: null,
  predictiveStates: null,
  search: null,
  language: null,
  reports: null,
};

const DomainTabs: FC<IDomainTabs> = ({
  surveyOverviewType,
  defaultTab,
  defaultDomain,
  applyFilters,
  children,
}) => {
  const showContentsTabs =
    surveyOverviewType === 'EFFECT' && defaultTab === CreateSurveyCardsNames.contents;

  const showReportsTabs = defaultTab === CreateSurveyCardsNames.participantsandreports;

  const showDashboardTabs = defaultTab === CreateSurveyCardsNames.dashboard;

  const { t } = useTranslation();

  const getApplyFilters = (card: string) => (domain: string) => () =>
    applyFilters({
      ...clearedFilters,
      card,
      domain,
    });

  const isActive = (domain: string) => defaultDomain === domain;

  if (showContentsTabs) {
    const getApplyContentsFilters = getApplyFilters('contents');

    const internalName = CreateSurveyTypeNames.internal;
    const externalName = CreateSurveyTypeNames.external;

    return (
      <Container>
        <TabsCont defaultValue={t('viewSurvey.topBar.internal')} data-testid="tabsContainer">
          <TabButton
            onClick={getApplyContentsFilters(internalName)}
            isSelected={isActive(internalName)}
            data-testid="tab"
          >
            {t('viewSurvey.topBar.internal')}
          </TabButton>
          <TabButton
            onClick={getApplyContentsFilters(externalName)}
            isSelected={isActive(externalName)}
            data-testid="tab"
          >
            {t('viewSurvey.topBar.external')}
          </TabButton>
        </TabsCont>
        <Children>{children}</Children>
      </Container>
    );
  }

  if (showReportsTabs) {
    const getApplyReportsFilters = getApplyFilters('participantsandreports');

    const participantsName = CreateSurveyTypeNames.participants;
    const reportsName = CreateSurveyTypeNames.reportsSetup;

    return (
      <Container>
        <TabsCont defaultValue={t('viewSurvey.topBar.participants')} data-testid="tabsContainer">
          <TabButton
            onClick={getApplyReportsFilters(reportsName)}
            isSelected={reportsName === defaultDomain}
            data-testid="tab"
          >
            {t('viewSurvey.topBar.reportsSetup')}
          </TabButton>
          <TabButton
            onClick={getApplyReportsFilters(participantsName)}
            isSelected={participantsName === defaultDomain}
            data-testid="tab"
          >
            {t('viewSurvey.topBar.participants')}
          </TabButton>
        </TabsCont>
        {children}
      </Container>
    );
  }

  if (showDashboardTabs) {
    const getApplyDashboardFilters = getApplyFilters('dashboard');

    const overviewName = CreateSurveyTypeNames.overview;
    const departmentsName = CreateSurveyTypeNames.departments;

    return (
      <Container>
        <TabsCont defaultValue={t('viewSurvey.topBar.overview')} data-testid="tabsContainer">
          <TabButton
            onClick={getApplyDashboardFilters(overviewName)}
            isSelected={overviewName === defaultDomain}
            data-testid="tab"
          >
            {t('viewSurvey.topBar.overview')}
          </TabButton>
          <TabButton
            onClick={getApplyDashboardFilters(departmentsName)}
            isSelected={departmentsName === defaultDomain}
            data-testid="tab"
          >
            {t('viewSurvey.topBar.departments')}
          </TabButton>
        </TabsCont>
        {children}
      </Container>
    );
  }

  if (defaultTab === CreateSurveyCardsNames.contents) {
    // ADAPT / PULSE: no Internal/External switch, but same card layout as EFFECT
    return (
      <Container>
        <ContentsCard>{children}</ContentsCard>
      </Container>
    );
  }

  return <Container>{children}</Container>;
};

export default DomainTabs;
